import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import {
  ConsoleHeader,
  MasterSealPanel,
  ArchivistsPanel,
  SealsPanel,
  IntruderForcePanel,
  AuditLogPanel,
} from "@/components/admin/ConsoleSections";

export default function AdminConsole() {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();

  const isMasterKey = user?.role === "master";

  const [admins, setAdmins] = useState([]);
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");
  const [auditActor, setAuditActor] = useState("");

  const [master, setMaster] = useState("");
  const [manualUnlock, setManualUnlock] = useState(false);
  const masterUnlocked = isMasterKey || manualUnlock;

  const [newUsername, setNewUsername] = useState("");
  const [newAlias, setNewAlias] = useState("");
  const [newRole, setNewRole] = useState("admin");

  // seal-form drafts
  const [vaultPass, setVaultPass] = useState("");
  const [masterPass, setMasterPass] = useState("");
  const [griddPass, setGriddPass] = useState("");

  const masterHeaders = () =>
    isMasterKey ? {} : { "X-Master-Passphrase": master };

  const reloadAdmins = () => api.get("/admins").then((r) => setAdmins(r.data));
  const reloadSettings = (headers = undefined) =>
    api.get("/settings", headers ? { headers } : undefined).then((r) => {
      setSettings(r.data);
      if (r.data.keys_revealed) {
        setVaultPass(r.data.vault_passphrase || "");
        setMasterPass(r.data.master_passphrase || "");
        setGriddPass(r.data.gridd_passphrase || "");
      } else {
        setVaultPass(r.data.vault_passphrase || "");
        setMasterPass("");
        setGriddPass("");
      }
    });
  const reloadAudit = () =>
    api.get("/audit", { params: { limit: 100 } }).then((r) => setAudit(r.data));

  useEffect(() => {
    if (user === null) return;
    if (!isAdmin) {
      navigate("/admin");
      return;
    }
    reloadAdmins();
    reloadSettings(isMasterKey ? {} : undefined);
    reloadAudit();
  }, [isAdmin, isMasterKey, user, navigate]);

  const tryUnlock = async () => {
    if (!master) return;
    try {
      const r = await api.get("/settings", {
        headers: { "X-Master-Passphrase": master },
      });
      if (r.data?.keys_revealed) {
        setSettings(r.data);
        setVaultPass(r.data.vault_passphrase || "");
        setMasterPass(r.data.master_passphrase || "");
        setGriddPass(r.data.gridd_passphrase || "");
        setManualUnlock(true);
        toast.success("Master seal accepted.");
        return;
      }
      toast.error("Master passphrase rejected.");
    } catch {
      toast.error("Master passphrase rejected.");
    }
  };

  const onReSeal = () => {
    setManualUnlock(false);
    setMaster("");
    setMasterPass("");
    setGriddPass("");
    reloadSettings();
  };

  const addAdmin = async () => {
    if (!masterUnlocked) {
      toast.error("Master passphrase required.");
      return;
    }
    if (!newUsername.trim() || !newAlias.trim()) {
      toast.error("A-file number and alias required.");
      return;
    }
    try {
      await api.post(
        "/admins",
        {
          username: newUsername.trim().toUpperCase(),
          alias: newAlias.trim(),
          role: newRole,
        },
        { headers: masterHeaders() }
      );
      setNewUsername("");
      setNewAlias("");
      setNewRole("admin");
      toast.success("Archivist inscribed.");
      reloadAdmins();
      reloadAudit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed.");
    }
  };

  const removeAdmin = async (id, username) => {
    if (!masterUnlocked) {
      toast.error("Master passphrase required.");
      return;
    }
    if (!window.confirm(`Strike ${username} from the rolls?`)) return;
    try {
      await api.delete(`/admins/${id}`, { headers: masterHeaders() });
      toast.success("Archivist struck.");
      reloadAdmins();
      reloadAudit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed.");
    }
  };

  const saveSettings = async () => {
    if (!masterUnlocked) {
      toast.error("Master passphrase required.");
      return;
    }
    try {
      await api.put(
        "/settings",
        { vault_passphrase: vaultPass, master_passphrase: masterPass, gridd_passphrase: griddPass },
        { headers: masterHeaders() }
      );
      if (!isMasterKey && masterPass !== master) {
        setMaster(masterPass);
      }
      toast.success("Seals reforged.");
      const nextHeaders = isMasterKey
        ? {}
        : { "X-Master-Passphrase": masterPass || master };
      await reloadSettings(nextHeaders);
      reloadAudit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    }
  };

  const toggleIntruderForce = async () => {
    if (!masterUnlocked) {
      toast.error("Master passphrase required to flip the trapdoor.");
      return;
    }
    const next = !settings.intruder_force_enabled;
    try {
      await api.put(
        "/settings",
        { intruder_force_enabled: next },
        { headers: masterHeaders() }
      );
      toast.success(next ? "Intruder Force armed." : "Intruder Force sealed.");
      await reloadSettings(isMasterKey ? {} : { "X-Master-Passphrase": master });
      reloadAudit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Toggle failed.");
    }
  };

  const filteredAudit = useMemo(() => {
    return audit.filter((e) => {
      if (auditFilter && e.action !== auditFilter) return false;
      if (auditActor && !e.actor_username.toUpperCase().includes(auditActor.toUpperCase())) return false;
      return true;
    });
  }, [audit, auditFilter, auditActor]);

  const actionOptions = useMemo(() => {
    const set = new Set(audit.map((e) => e.action));
    return Array.from(set).sort();
  }, [audit]);

  const refreshAudit = () => {
    setAuditFilter("");
    setAuditActor("");
    reloadAudit();
  };

  if (!isAdmin || !settings) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: validating archivist :::</div>;
  }

  return (
    <div className="space-y-14" data-testid="admin-console-page">
      <ConsoleHeader user={user} isMasterKey={isMasterKey} />

      <MasterSealPanel
        isMasterKey={isMasterKey}
        masterUnlocked={masterUnlocked}
        master={master}
        setMaster={setMaster}
        tryUnlock={tryUnlock}
        onReSeal={onReSeal}
      />

      <ArchivistsPanel
        admins={admins}
        user={user}
        masterUnlocked={masterUnlocked}
        isMasterKey={isMasterKey}
        newUsername={newUsername}
        setNewUsername={setNewUsername}
        newAlias={newAlias}
        setNewAlias={setNewAlias}
        newRole={newRole}
        setNewRole={setNewRole}
        onAdd={addAdmin}
        onRemove={removeAdmin}
      />

      <SealsPanel
        settings={settings}
        masterUnlocked={masterUnlocked}
        vaultPass={vaultPass}
        setVaultPass={setVaultPass}
        masterPass={masterPass}
        setMasterPass={setMasterPass}
        griddPass={griddPass}
        setGriddPass={setGriddPass}
        onSave={saveSettings}
      />

      <IntruderForcePanel
        settings={settings}
        masterUnlocked={masterUnlocked}
        onToggle={toggleIntruderForce}
      />

      <AuditLogPanel
        audit={audit}
        filteredAudit={filteredAudit}
        actionOptions={actionOptions}
        auditFilter={auditFilter}
        setAuditFilter={setAuditFilter}
        auditActor={auditActor}
        setAuditActor={setAuditActor}
        onRefresh={refreshAudit}
      />

      <div className="border-t border-[#333] pt-6 text-[0.6rem] tracking-[0.4em] uppercase text-[#555] text-center">
        <Link to="/" className="hover:text-[#E3E1D9]">↩ Return to surface</Link>
      </div>
    </div>
  );
}
